import { createContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { startLogin } from "@/lib/auth";
import { useNavigate } from "@tanstack/react-router";

interface AuthUser {
  type: "google_user";
  properties: {
    email: string;
    email_verified: boolean;
    name?: string;
    picture?: string;
  };
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
  refetchUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const refetchUser = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/auth/me");
      const data = await response.json();
      setUser(data.user);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch current user on mount
    refetchUser();
  }, [refetchUser]);

  const login = () => startLogin();

  const logout = async () => {
    await fetch("/auth/logout", { method: "POST" });
    setUser(null);
    navigate({ to: "/" });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}
